import React from "react";

// =====================================================================
// ErrorBoundary (S53) — captura QUALQUER crash de render na arvore e
// mostra uma tela amigavel com "Recarregar" em vez de TELA BRANCA.
//
// Motivacao: o crash do `ch` do Resgate (Ursula/Rostand) deixava a tela
// totalmente branca (React desmonta a arvore num erro nao capturado).
// Com este boundary, qualquer erro de render vira uma tela recuperavel.
//
// Decisoes:
//   - Class component (unica forma de error boundary no React 16/17).
//   - Estilos INLINE puros (sem MUI/tema) — renderiza mesmo se o proprio
//     ThemeProvider/estilo quebrar.
//   - NAO mexe em localStorage automaticamente (um crash qualquer nao deve
//     deslogar o usuario). Oferece "Recarregar" (resolve a maioria) e um
//     escape "Ir para o login" (re-login limpo p/ o caso cross-tenant).
// =====================================================================
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: (error && error.message) || "Erro inesperado",
    };
  }

  componentDidCatch(error, info) {
    // Log pra console/telemetria — nao expoe stack pro usuario.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] crash de render capturado:", error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleLogin = () => {
    window.location.href = "/login";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const wrap = {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
      background: "#f4f6f8",
      fontFamily:
        'Inter, Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
    };
    const card = {
      maxWidth: "440px",
      width: "100%",
      background: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
      padding: "32px",
      textAlign: "center",
      boxSizing: "border-box",
    };
    const emoji = { fontSize: "44px", lineHeight: 1, marginBottom: "12px" };
    const title = {
      fontSize: "1.25rem",
      fontWeight: 700,
      color: "#1f2937",
      margin: "0 0 8px",
    };
    const text = {
      fontSize: "0.95rem",
      color: "#4b5563",
      lineHeight: 1.5,
      margin: "0 0 24px",
    };
    const row = {
      display: "flex",
      gap: "12px",
      justifyContent: "center",
      flexWrap: "wrap",
    };
    const btnBase = {
      border: "none",
      borderRadius: "8px",
      padding: "10px 20px",
      fontSize: "0.95rem",
      fontWeight: 600,
      cursor: "pointer",
    };
    const btnPrimary = { ...btnBase, background: "#065183", color: "#ffffff" };
    const btnGhost = {
      ...btnBase,
      background: "transparent",
      color: "#065183",
      textDecoration: "underline",
    };

    return (
      <div style={wrap} role="alert">
        <div style={card}>
          <div style={emoji}>⚠️</div>
          <h1 style={title}>Algo deu errado nesta tela</h1>
          <p style={text}>
            A tela encontrou um problema e foi interrompida. Seus dados estão
            seguros — em geral basta recarregar pra continuar. Se o erro
            persistir, saia e entre novamente.
          </p>
          <div style={row}>
            <button style={btnPrimary} onClick={this.handleReload}>
              Recarregar agora
            </button>
            <button style={btnGhost} onClick={this.handleLogin}>
              Ir para o login
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
